import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ICostUnit } from '../../../models/cost-unit.model';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { CostUnitDialogComponent } from './cost-unit-dialog/cost-unit-dialog.component';
import { ICostUnitState } from '../../../services/facades/master-data/cost-unit-state.model';
import { CostUnitFacade } from '../../../services/facades/master-data/cost-unit.facade';
import { distinctUntilChanged, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import { MatSort } from '@angular/material/sort';

@Component({
    templateUrl: './cost-unit.page.html',
    styleUrls: [ './cost-unit.page.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostUnitPage implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(MatPaginator)
    public paginator: MatPaginator;
    @ViewChild(MatSort)
    public sort: MatSort;

    public dataSource: MatTableDataSource<ICostUnit> = new MatTableDataSource<ICostUnit>([]);
    public costUnitState$: Observable<ICostUnitState>;
    public sharedDataState$: Observable<ISharedDataState>;
    private destroy$: Subject<void> = new Subject();

    constructor(private myCostUnitFacade: CostUnitFacade,
                private myMatDialog: MatDialog,
                private mySharedDataFacade: SharedDataFacade) {
    }

    public ngOnInit(): void {
        this.costUnitState$ = this.myCostUnitFacade.subState();
        this.sharedDataState$ = this.mySharedDataFacade.subState();
        this.sharedDataState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ISharedDataState) => s.costUnitLookup),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data:  Record<number, ICostUnit>) => {
                this.dataSource.data = Object.values(data);
            });
        void firstValueFrom(this.mySharedDataFacade.fetchCostUnits());
    }

    public ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public onEdit(costUnit: ICostUnit): void {
        const dialog: MatDialogRef<CostUnitDialogComponent> = this.myMatDialog.open(CostUnitDialogComponent, {
            width: '600px',
            data: costUnit,
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void firstValueFrom(this.mySharedDataFacade.fetchCostUnits(true));
            }
        });
    }

    public onCreate(): void {
        const dialog: MatDialogRef<CostUnitDialogComponent> = this.myMatDialog.open(CostUnitDialogComponent, {
            width: '600px',
            data: {},
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void firstValueFrom(this.mySharedDataFacade.fetchCostUnits(true));
            }
        });
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

    public onDeleteCostUnit(costUnit: ICostUnit): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.cost-unit.confirm-delete.label',
                messageParams: { name: costUnit.name }
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myCostUnitFacade.deleteCostUnit(costUnit.id);
                void firstValueFrom(this.mySharedDataFacade.fetchCostUnits(true));
            }
        });
    }

}
